/** Parse SVG intrinsic size from viewBox / width / height. */
export function parseSvgBoardSize(svg: string): { width: number; height: number } {
  const raw = svg.trim();
  const viewBox = raw.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (viewBox?.[1]) {
    const parts = viewBox[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length >= 4 && parts[2]! > 0 && parts[3]! > 0) {
      return normalizeBoardSize(parts[2]!, parts[3]!);
    }
  }

  const wMatch = raw.match(/\bwidth\s*=\s*["']([0-9.]+)/i);
  const hMatch = raw.match(/\bheight\s*=\s*["']([0-9.]+)/i);
  const w = wMatch ? Number(wMatch[1]) : NaN;
  const h = hMatch ? Number(hMatch[1]) : NaN;
  if (w > 0 && h > 0) return normalizeBoardSize(w, h);

  return { width: 1000, height: 700 };
}

/** Comfortable base size from SVG upload (longest side ≤1400). */
export function normalizeBoardSize(width: number, height: number): { width: number; height: number } {
  return clampBoardSize(width, height, 1400);
}

/**
 * Keep aspect; enforce min side ≥200 and max side ≤ maxSide.
 * Does NOT shrink boards that already fit — so 150% scale persists.
 */
export function clampBoardSize(
  width: number,
  height: number,
  maxSide = 5000,
): { width: number; height: number } {
  let w = Math.max(1, width);
  let h = Math.max(1, height);
  const longest = Math.max(w, h);
  if (longest > maxSide) {
    const s = maxSide / longest;
    w *= s;
    h *= s;
  }
  const shortest = Math.min(w, h);
  if (shortest < 200) {
    const s = 200 / shortest;
    w *= s;
    h *= s;
  }
  return {
    width: Math.max(200, Math.round(w)),
    height: Math.max(200, Math.round(h)),
  };
}

function readRootViewBox(svg: string): { x: number; y: number; w: number; h: number } | null {
  const viewBox = svg.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (!viewBox?.[1]) return null;
  const parts = viewBox[1].trim().split(/[\s,]+/).map(Number);
  if (parts.length >= 4 && parts[2]! > 0 && parts[3]! > 0) {
    return { x: parts[0] ?? 0, y: parts[1] ?? 0, w: parts[2]!, h: parts[3]! };
  }
  return null;
}

function readRootViewBoxSize(svg: string): { width: number; height: number } | null {
  const vb = readRootViewBox(svg);
  return vb ? { width: vb.w, height: vb.h } : null;
}

function readRootPixelSize(svg: string): { width: number; height: number } | null {
  const wMatch = svg.match(/\bwidth\s*=\s*["']([0-9.]+)(?:px)?["']/i);
  const hMatch = svg.match(/\bheight\s*=\s*["']([0-9.]+)(?:px)?["']/i);
  const w = wMatch ? Number(wMatch[1]) : NaN;
  const h = hMatch ? Number(hMatch[1]) : NaN;
  if (w > 0 && h > 0) return { width: w, height: h };
  return null;
}

export type FloorplanCropInsets = {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
};

/** Default visibility for underlay SVG slices. Contours off = hide empty black outlines. */
export const DEFAULT_UNDERLAY_SVG_LAYERS = {
  fills: true,
  texts: true,
  grid: true,
  contours: false,
  decor: true,
} as const;

export type UnderlaySvgLayerId = keyof typeof DEFAULT_UNDERLAY_SVG_LAYERS;
export type UnderlaySvgLayerFlags = Partial<Record<UnderlaySvgLayerId, boolean>>;

export type PrepareFloorplanOptions = {
  /** Preferred board size (hall.floorplanWidth/Height). Required for reliable <img> rendering. */
  width?: number;
  height?: number;
  /** Trim A4 margins as % of original viewBox (0–45 per side). */
  crop?: FloorplanCropInsets;
  /** Which underlay SVG slices to show (grid/contours/fills/…). */
  underlayLayers?: UnderlaySvgLayerFlags;
};

function clampCropPct(n: number | undefined): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(45, Math.max(0, Number(n)));
}

export function normalizeCropInsets(crop?: FloorplanCropInsets | null): {
  left: number;
  top: number;
  right: number;
  bottom: number;
} {
  return {
    left: clampCropPct(crop?.left),
    top: clampCropPct(crop?.top),
    right: clampCropPct(crop?.right),
    bottom: clampCropPct(crop?.bottom),
  };
}

/** Axis-aligned booth rectangle in % of the SVG viewBox. */
export type FloorplanBoothRect = {
  /** Left edge, 0–100 */
  x: number;
  /** Top edge, 0–100 */
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

/** Normalize SVG fill to #RRGGBB (upper) when possible. */
export function normalizeSvgFill(raw: string | null | undefined): string {
  const f = String(raw ?? "").trim();
  if (!f || /^none$/i.test(f)) return "";
  if (/^#[0-9a-f]{6}$/i.test(f)) return f.toUpperCase();
  if (/^#[0-9a-f]{3}$/i.test(f)) {
    const h = f.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
  }
  const rgb = f.match(/rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)/i);
  if (rgb) {
    const r = Math.max(0, Math.min(255, Math.round(Number(rgb[1]))));
    const g = Math.max(0, Math.min(255, Math.round(Number(rgb[2]))));
    const b = Math.max(0, Math.min(255, Math.round(Number(rgb[3]))));
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`.toUpperCase();
  }
  return f.toUpperCase();
}

/** Pastel pavilion booth fills (current Corel palette). */
const PASTEL_BOOTH_FILLS = new Set([
  "#D1CDE6",
  "#C9C4E0",
  "#9EDBF4",
  "#A7D4AC",
  "#B8D4C8",
  "#E8E4F5",
  "#D6EAF8",
]);

/**
 * Legacy Corel/LO coral layer — often exported as a duplicate under pastel cells.
 * Same count as pastel on pavilion plans → visual "orange trash" if both drawn.
 */
const LEGACY_CORAL_BOOTH_FILLS = new Set([
  "#F08061",
  "#FBD7DB",
  "#F5C6CB",
]);

export function isLegacyCoralBoothFill(raw: string | null | undefined): boolean {
  const fill = normalizeSvgFill(raw);
  if (!fill || fill[0] !== "#") return false;
  if (LEGACY_CORAL_BOOTH_FILLS.has(fill)) return true;
  const r = parseInt(fill.slice(1, 3), 16);
  const g = parseInt(fill.slice(3, 5), 16);
  const b = parseInt(fill.slice(5, 7), 16);
  if (![r, g, b].every(Number.isFinite)) return false;
  // Classic LO coral #F08061 neighborhood.
  return r >= 220 && g >= 90 && g <= 160 && b >= 70 && b <= 140 && r > g + 40 && r > b + 40;
}

export function isPastelBoothFill(raw: string | null | undefined): boolean {
  const fill = normalizeSvgFill(raw);
  if (!fill || fill[0] !== "#") return false;
  if (PASTEL_BOOTH_FILLS.has(fill)) return true;
  if (isLegacyCoralBoothFill(fill)) return false;
  const r = parseInt(fill.slice(1, 3), 16);
  const g = parseInt(fill.slice(3, 5), 16);
  const b = parseInt(fill.slice(5, 7), 16);
  if (![r, g, b].every(Number.isFinite)) return false;
  if (r >= 245 && g >= 245 && b >= 245) return false;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const light = (max + min) / 2 / 255;
  const sat = max === 0 ? 0 : (max - min) / max;
  if (light >= 0.58 && light <= 0.92 && sat >= 0.06 && sat <= 0.45) {
    if (g > r + 30 && g > b + 30 && r > 150) return false;
    return true;
  }
  return false;
}

/** True when SVG has a modern pastel booth layer (not only coral). */
export function svgHasPastelBoothLayer(svg: string): boolean {
  const s = String(svg ?? "");
  if (!s) return false;
  let n = 0;
  for (const m of s.matchAll(/fill\s*=\s*["']([^"']+)["']/gi)) {
    if (isPastelBoothFill(m[1])) {
      n += 1;
      if (n >= 5) return true;
    }
  }
  return false;
}

/**
 * Remove duplicate coral booth shapes when pastel cells exist.
 * Setting fill=none left black stroke contours — delete the whole element.
 * Corel often keeps an old coral layer under pastel stands; LO exports both.
 */
export function stripLegacyCoralBoothFills(svg: string): string {
  const s0 = String(svg ?? "");
  if (!s0 || !svgHasPastelBoothLayer(s0)) return s0;
  // Self-closing path/rect/polygon with coral fill.
  let s = s0.replace(
    /<(path|rect|polygon)\b([^>]*?)\/>/gi,
    (full, _tag: string, attrs: string) => {
      const fill = attrs.match(/\bfill\s*=\s*["']([^"']+)["']/i)?.[1];
      return fill && isLegacyCoralBoothFill(fill) ? "" : full;
    },
  );
  // Non-self-closing variants.
  s = s.replace(
    /<(path|rect|polygon)\b([^>]*?)>([\s\S]*?)<\/\1>/gi,
    (full, _tag: string, attrs: string) => {
      const fill = attrs.match(/\bfill\s*=\s*["']([^"']+)["']/i)?.[1];
      return fill && isLegacyCoralBoothFill(fill) ? "" : full;
    },
  );
  return s;
}

/**
 * Booth cell fills from Corel / LibreOffice exports.
 * Plans mix coral, lavender, light-blue and soft-green cells — not only coral.
 * When a pastel layer is present, coral is treated as phantom (not a booth).
 */
export function isBoothCellFill(
  raw: string | null | undefined,
  want?: string,
  options?: { allowCoral?: boolean },
): boolean {
  const fill = normalizeSvgFill(raw);
  if (!fill || fill.length < 7 || fill[0] !== "#") return false;
  if (want) return fill === normalizeSvgFill(want);

  if (isPastelBoothFill(fill)) return true;

  // Exact legacy pinks / coral — only when caller allows (no pastel layer).
  if (LEGACY_CORAL_BOOTH_FILLS.has(fill) || isLegacyCoralBoothFill(fill)) {
    return options?.allowCoral !== false;
  }

  return false;
}

/** Near-black / dark charcoal stroke used on line-art pavilion plans. */
export function isDarkBoothStroke(raw: string | null | undefined): boolean {
  const stroke = normalizeSvgFill(raw);
  if (!stroke || stroke[0] !== "#") return false;
  if (stroke === "#000000" || stroke === "#000" || stroke === "#222628") return true;
  const r = parseInt(stroke.slice(1, 3), 16);
  const g = parseInt(stroke.slice(3, 5), 16);
  const b = parseInt(stroke.slice(5, 7), 16);
  if (![r, g, b].every(Number.isFinite)) return false;
  return r <= 60 && g <= 60 && b <= 60;
}

/**
 * Black-scheme / line-art booth cell: white (or none) fill + dark stroke,
 * or solid dark fill used for conference halls on PCV-style exports.
 */
export function isOutlineBoothShapeAttrs(
  attrs: string,
  options?: { allowDarkFill?: boolean },
): boolean {
  const fillRaw = attrs.match(/\bfill\s*=\s*["']([^"']+)["']/i)?.[1];
  const strokeRaw = attrs.match(/\bstroke\s*=\s*["']([^"']+)["']/i)?.[1];
  const fill = normalizeSvgFill(fillRaw);
  const stroke = normalizeSvgFill(strokeRaw);

  if (stroke && isDarkBoothStroke(stroke)) {
    if (!fill || fill === "#FFFFFF" || fill === "#FFF") return true;
    // Very light fills with black outline.
    if (fill[0] === "#") {
      const r = parseInt(fill.slice(1, 3), 16);
      const g = parseInt(fill.slice(3, 5), 16);
      const b = parseInt(fill.slice(5, 7), 16);
      if ([r, g, b].every(Number.isFinite) && (r + g + b) / 3 >= 230) return true;
    }
  }

  // Dark solid stand (conference hall) without pastel — only when enabled.
  if (options?.allowDarkFill && fill && isDarkBoothStroke(fill) && !stroke) {
    return true;
  }

  return false;
}

function pathDataBBox(d: string): { minx: number; miny: number; maxx: number; maxy: number } | null {
  const xs: number[] = [];
  const ys: number[] = [];
  // LibreOffice uses "M x,y L x,y x,y x,y Z" — coordinate pairs after the command letter.
  const tokens = d.match(/[MmLlHhVvZz]|-?[\d.]+(?:e[-+]?\d+)?/g) ?? [];
  let mode = "";
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i]!;
    if (/^[MmLlHhVvZz]$/.test(t)) {
      mode = t;
      i += 1;
      continue;
    }
    if (mode === "H" || mode === "h") {
      const x = Number(t);
      if (Number.isFinite(x)) xs.push(x);
      i += 1;
      continue;
    }
    if (mode === "V" || mode === "v") {
      const y = Number(t);
      if (Number.isFinite(y)) ys.push(y);
      i += 1;
      continue;
    }
    if (mode === "Z" || mode === "z") {
      i += 1;
      continue;
    }
    // M/L and implicit continuations: x y pairs
    const x = Number(t);
    const y = Number(tokens[i + 1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      xs.push(x);
      ys.push(y);
      i += 2;
      continue;
    }
    i += 1;
  }
  if (xs.length < 2 || ys.length < 2) return null;
  return {
    minx: Math.min(...xs),
    miny: Math.min(...ys),
    maxx: Math.max(...xs),
    maxy: Math.max(...ys),
  };
}

/**
 * Extract booth cell rectangles from Corel / LibreOffice SVG
 * as percent of viewBox (pink/coral fills on polygon / rect / path).
 */
export function parseBoothRectsFromSvg(
  svg: string,
  options?: { fill?: string },
): FloorplanBoothRect[] {
  const s = String(svg ?? "");
  if (!s) return [];
  const vb = readRootViewBox(s) ?? { x: 0, y: 0, w: 1000, h: 700 };
  const out: FloorplanBoothRect[] = [];
  // Dual-layer CDRs: ignore coral phantoms when pastel stands exist.
  const allowCoral = options?.fill
    ? true
    : !svgHasPastelBoothLayer(s);

  const pushBox = (minx: number, miny: number, maxx: number, maxy: number) => {
    const bw = maxx - minx;
    const bh = maxy - miny;
    // Skip hairlines and full-page backgrounds.
    if (bw < 8 || bh < 8) return;
    if (bw > vb.w * 0.95 && bh > vb.h * 0.95) return;
    out.push({
      x: ((minx - vb.x) / vb.w) * 100,
      y: ((miny - vb.y) / vb.h) * 100,
      w: (bw / vb.w) * 100,
      h: (bh / vb.h) * 100,
      cx: (((minx + maxx) / 2 - vb.x) / vb.w) * 100,
      cy: (((miny + maxy) / 2 - vb.y) / vb.h) * 100,
    });
  };

  const useOutlineFallback = !options?.fill && !svgHasPastelBoothLayer(s);

  const fillOk = (attrs: string) => {
    if (
      isBoothCellFill(attrs.match(/fill="([^"]+)"/i)?.[1], options?.fill, {
        allowCoral,
      })
    ) {
      return true;
    }
    // Black/white pavilion schemes: cells are white fill + black stroke (no pastel).
    if (useOutlineFallback) {
      return isOutlineBoothShapeAttrs(attrs, { allowDarkFill: true });
    }
    return false;
  };

  for (const m of s.matchAll(/<polygon\b([^>]*)\/?>/gi)) {
    const attrs = m[1] ?? "";
    if (!fillOk(attrs)) continue;
    const ptsM = attrs.match(/points="([^"]+)"/i);
    if (!ptsM?.[1]) continue;
    const nums = ptsM[1]
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => Number.isFinite(n));
    if (nums.length < 6) continue;
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
      xs.push(nums[i]!);
      ys.push(nums[i + 1]!);
    }
    pushBox(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
  }

  for (const m of s.matchAll(/<rect\b([^>]*)\/?>/gi)) {
    const attrs = m[1] ?? "";
    if (!fillOk(attrs)) continue;
    // LibreOffice uses empty BoundingBox rects with fill=none — already skipped.
    const x = Number(attrs.match(/\bx="([^"]+)"/i)?.[1]);
    const y = Number(attrs.match(/\by="([^"]+)"/i)?.[1]);
    const w = Number(attrs.match(/\bwidth="([^"]+)"/i)?.[1]);
    const h = Number(attrs.match(/\bheight="([^"]+)"/i)?.[1]);
    if (![x, y, w, h].every(Number.isFinite) || w < 1 || h < 1) continue;
    pushBox(x, y, x + w, y + h);
  }

  // LibreOffice CDR→SVG: booth cells are <path fill="rgb(240,128,97)" d="M …"/>.
  for (const m of s.matchAll(/<path\b([^>]*)\/?>/gi)) {
    const attrs = m[1] ?? "";
    if (!fillOk(attrs)) continue;
    const d = attrs.match(/\bd="([^"]+)"/i)?.[1];
    if (!d) continue;
    const box = pathDataBBox(d);
    if (!box) continue;
    pushBox(box.minx, box.miny, box.maxx, box.maxy);
  }

  return out;
}

/** Normalize booth codes for matching: "A-301", "a 301" → "A301". */
/**
 * Corel / RU pavilion plans often label stands with Cyrillic lookalikes
 * (А105, Т210, М602) while Tezam/Manager stores Latin A/T/M.
 */
const CYRILLIC_BOOTH_LETTER: Record<string, string> = {
  А: "A",
  В: "B",
  Е: "E",
  К: "K",
  М: "M",
  Н: "H",
  О: "O",
  Р: "P",
  С: "C",
  Т: "T",
  Х: "X",
  а: "A",
  в: "B",
  е: "E",
  к: "K",
  м: "M",
  н: "H",
  о: "O",
  р: "P",
  с: "C",
  т: "T",
  х: "X",
};

export function normalizeBoothCode(raw: string | null | undefined): string {
  const mapped = String(raw ?? "")
    .trim()
    .replace(/[–—−]/g, "-")
    .replace(/\s+/g, "")
    .replace(/-/g, "")
    .replace(/[АВЕКМНОРСТХавекмнорстх]/g, (ch) => CYRILLIC_BOOTH_LETTER[ch] ?? ch);
  return mapped.toUpperCase();
}

/** Letter booth codes used on modern pavilion plans (A301, B12…). */
export function looksLikeLetterBoothCode(raw: string | null | undefined): boolean {
  const code = normalizeBoothCode(raw);
  if (!code || code.length > 8) return false;
  return /^[A-Z]{1,2}\d{2,4}[A-Z]?$/.test(code);
}

/**
 * Area / sq.m markers baked into Corel cells (12, 30, 54…) — not booth IDs.
 * Kept separate so we never promote them to stand numbers when A### codes exist.
 */
export function looksLikeBoothAreaNumber(raw: string | null | undefined): boolean {
  const t = String(raw ?? "").trim();
  if (!/^\d{1,3}$/.test(t)) return false;
  const n = Number(t);
  return n >= 1 && n <= 500;
}

/** True for typical pavilion booth labels (A301, A-413b; bare digits only as last resort). */
export function looksLikeBoothCode(raw: string | null | undefined): boolean {
  const code = normalizeBoothCode(raw);
  if (!code || code.length > 8) return false;
  if (/^(19|20)\d{2}$/.test(code)) return false;
  if (looksLikeLetterBoothCode(code)) return true;
  return /^\d{2,3}$/.test(code);
}

export type FloorplanBoothLabel = {
  code: string;
  /** Label anchor X, % of viewBox */
  x: number;
  /** Label anchor Y, % of viewBox */
  y: number;
};

/** SVG allows space/comma-separated length lists (glyph x advances on <text>). */
export function parseSvgNumberList(raw: string | null | undefined): number[] {
  const s = String(raw ?? "").trim();
  if (!s) return [];
  return s
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
}

/**
 * Anchor for SVG text x/y attrs.
 * Single value → that point; multi-value glyph list → midpoint of first/last
 * (Corel / reconstructed stand labels often use per-glyph x lists).
 */
export function svgListCoordAnchor(
  rawX: string | null | undefined,
  rawY: string | null | undefined,
): { x: number; y: number } | null {
  const xs = parseSvgNumberList(rawX);
  const ys = parseSvgNumberList(rawY);
  if (!xs.length && !ys.length) return null;
  const x =
    xs.length === 0
      ? 0
      : xs.length === 1
        ? xs[0]!
        : (xs[0]! + xs[xs.length - 1]!) / 2;
  const y =
    ys.length === 0
      ? 0
      : ys.length === 1
        ? ys[0]!
        : (ys[0]! + ys[ys.length - 1]!) / 2;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

type SvgMatrix2d = { a: number; b: number; c: number; d: number; e: number; f: number };

function parseSvgMatrix(attrs: string): SvgMatrix2d {
  const identity = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  const tm = attrs.match(/transform="([^"]+)"/i);
  if (!tm?.[1]) return identity;
  const t = tm[1];
  const matrix = t.match(/matrix\(\s*([^)]+)\)/i);
  if (matrix?.[1]) {
    const parts = matrix[1].split(/[\s,]+/).map(Number);
    if (
      parts.length >= 6 &&
      parts.slice(0, 6).every((n) => Number.isFinite(n))
    ) {
      return {
        a: parts[0]!,
        b: parts[1]!,
        c: parts[2]!,
        d: parts[3]!,
        e: parts[4]!,
        f: parts[5]!,
      };
    }
  }
  const translate = t.match(/translate\(\s*([^)]+)\)/i);
  if (translate?.[1]) {
    const parts = translate[1].split(/[\s,]+/).map(Number);
    if (parts.length >= 1 && Number.isFinite(parts[0])) {
      return {
        a: 1,
        b: 0,
        c: 0,
        d: 1,
        e: parts[0]!,
        f: Number.isFinite(parts[1]) ? parts[1]! : 0,
      };
    }
  }
  return identity;
}

function applySvgMatrix(m: SvgMatrix2d, x: number, y: number): { x: number; y: number } {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

function textContentPlain(inner: string): string {
  return inner
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

/** Resolve text anchor from LO TextPosition tspan / BoundingBox / text attrs. */
function resolveTextAnchorPx(
  textAttrs: string,
  inner: string,
  precedingChunk: string,
): { x: number; y: number } | null {
  // LibreOffice: <tspan class="TextPosition" x="19078" y="12391">
  const pos = inner.match(
    /TextPosition[^>]*\bx="([^"]+)"[^>]*\by="([^"]+)"|\by="([^"]+)"[^>]*\bx="([^"]+)"/i,
  );
  if (pos) {
    const anchor = svgListCoordAnchor(pos[1] ?? pos[4], pos[2] ?? pos[3]);
    if (anchor) return anchor;
  }
  const anyTspan = inner.match(/\bx="([^"]+)"[^>]*\by="([^"]+)"/i);
  if (anyTspan) {
    const anchor = svgListCoordAnchor(anyTspan[1], anyTspan[2]);
    if (anchor) return anchor;
  }
  // Sibling BoundingBox just before the text group.
  const bb = precedingChunk.match(
    /<rect[^>]*class="BoundingBox"[^>]*\bx="([^"]+)"[^>]*\by="([^"]+)"[^>]*\bwidth="([^"]+)"[^>]*\bheight="([^"]+)"[^>]*>/i,
  );
  if (bb) {
    const x = Number(bb[1]) + Number(bb[3]) / 2;
    const y = Number(bb[2]) + Number(bb[4]) / 2;
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  const local = svgListCoordAnchor(
    textAttrs.match(/\bx="([^"]+)"/i)?.[1],
    textAttrs.match(/\by="([^"]+)"/i)?.[1],
  );
  const matrix = parseSvgMatrix(textAttrs);
  const hasMatrix =
    matrix.a !== 1 ||
    matrix.b !== 0 ||
    matrix.c !== 0 ||
    matrix.d !== 1 ||
    matrix.e !== 0 ||
    matrix.f !== 0;
  if (local || hasMatrix) {
    return applySvgMatrix(matrix, local?.x ?? 0, local?.y ?? 0);
  }
  return null;
}

/**
 * Extract booth-number text labels from Corel / LibreOffice SVG exports
 * as percent coordinates (viewBox space).
 */
export function parseBoothTextLabelsFromSvg(
  svg: string,
  options?: { letterCodesOnly?: boolean },
): FloorplanBoothLabel[] {
  const s = String(svg ?? "");
  if (!s) return [];
  const vb = readRootViewBox(s) ?? { x: 0, y: 0, w: 1000, h: 700 };
  const raw: FloorplanBoothLabel[] = [];

  for (const m of s.matchAll(/<text\b([^>]*)>([\s\S]*?)<\/text>/gi)) {
    const attrs = m[1] ?? "";
    const inner = m[2] ?? "";
    const plain = textContentPlain(inner);
    const dataCode = attrs.match(/\bdata-stand-id=["']([^"']+)["']/i)?.[1];
    const codeSrc = dataCode || plain;
    if (!looksLikeBoothCode(codeSrc)) continue;
    const idx = m.index ?? 0;
    const preceding = s.slice(Math.max(0, idx - 280), idx);
    const anchor = resolveTextAnchorPx(attrs, inner, preceding);
    if (!anchor) continue;
    // Reject bogus (0,0) anchors from missing coords.
    if (Math.abs(anchor.x) < 1 && Math.abs(anchor.y) < 1) continue;
    const xPct = ((anchor.x - vb.x) / vb.w) * 100;
    const yPct = ((anchor.y - vb.y) / vb.h) * 100;
    // Multi-value x lists used to parse as NaN→0 and stack on the left edge.
    if (xPct < -2 || xPct > 102 || yPct < -2 || yPct > 102) continue;
    raw.push({
      code: normalizeBoothCode(codeSrc),
      x: xPct,
      y: yPct,
    });
  }

  const hasLetter = raw.some((l) => looksLikeLetterBoothCode(l.code));
  const letterOnly = options?.letterCodesOnly ?? hasLetter;
  if (!letterOnly) return raw;
  // Drop sq.m / aisle numbers that Corel draws inside cells (12, 30, 54…).
  return raw.filter((l) => looksLikeLetterBoothCode(l.code));
}

export type LabeledBoothRect = FloorplanBoothRect & { code: string };

function shapeAttrsToBoothRect(
  tag: string,
  attrs: string,
  vb: { x: number; y: number; w: number; h: number },
): FloorplanBoothRect | null {
  let minx = NaN;
  let miny = NaN;
  let maxx = NaN;
  let maxy = NaN;

  if (/^polygon$/i.test(tag)) {
    const ptsM = attrs.match(/points="([^"]+)"/i);
    if (!ptsM?.[1]) return null;
    const nums = ptsM[1]
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => Number.isFinite(n));
    if (nums.length < 6) return null;
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
      xs.push(nums[i]!);
      ys.push(nums[i + 1]!);
    }
    minx = Math.min(...xs);
    miny = Math.min(...ys);
    maxx = Math.max(...xs);
    maxy = Math.max(...ys);
  } else if (/^rect$/i.test(tag)) {
    const x = Number(attrs.match(/\bx="([^"]+)"/i)?.[1]);
    const y = Number(attrs.match(/\by="([^"]+)"/i)?.[1]);
    const w = Number(attrs.match(/\bwidth="([^"]+)"/i)?.[1]);
    const h = Number(attrs.match(/\bheight="([^"]+)"/i)?.[1]);
    if (![x, y, w, h].every(Number.isFinite) || w < 1 || h < 1) return null;
    minx = x;
    miny = y;
    maxx = x + w;
    maxy = y + h;
  } else if (/^path$/i.test(tag)) {
    const d = attrs.match(/\bd="([^"]+)"/i)?.[1];
    if (!d) return null;
    const box = pathDataBBox(d);
    if (!box) return null;
    minx = box.minx;
    miny = box.miny;
    maxx = box.maxx;
    maxy = box.maxy;
  } else {
    return null;
  }

  const bw = maxx - minx;
  const bh = maxy - miny;
  if (bw < 8 || bh < 8) return null;
  if (bw > vb.w * 0.95 && bh > vb.h * 0.95) return null;
  return {
    x: ((minx - vb.x) / vb.w) * 100,
    y: ((miny - vb.y) / vb.h) * 100,
    w: (bw / vb.w) * 100,
    h: (bh / vb.h) * 100,
    cx: (((minx + maxx) / 2 - vb.x) / vb.w) * 100,
    cy: (((miny + maxy) / 2 - vb.y) / vb.h) * 100,
  };
}

/**
 * Semantic pavilion SVGs (PCV-style): shapes tagged with data-stand-id /
 * data-role="stand-shape". Prefer this over pastel-fill heuristics.
 */
export function parseSemanticStandRectsFromSvg(svg: string): LabeledBoothRect[] {
  const s = String(svg ?? "");
  if (!s || !/data-stand-id=/i.test(s)) return [];
  const vb = readRootViewBox(s) ?? { x: 0, y: 0, w: 1000, h: 700 };
  const out: LabeledBoothRect[] = [];
  const seen = new Set<string>();

  for (const m of s.matchAll(/<(polygon|path|rect)\b([^>]*)\/?>/gi)) {
    const tag = m[1] ?? "";
    const attrs = m[2] ?? "";
    const dataId = attrs.match(/\bdata-stand-id=["']([^"']+)["']/i)?.[1];
    const idShape = attrs.match(/\bid=["']stand-shape-([^"']+)["']/i)?.[1];
    const codeRaw = dataId || idShape;
    if (!codeRaw) continue;
    const role = attrs.match(/\bdata-role=["']([^"']+)["']/i)?.[1] ?? "";
    // Prefer explicit stand shapes; skip facility captions / icons.
    if (role && !/stand/i.test(role)) continue;
    const code = normalizeBoothCode(codeRaw);
    if (!looksLikeBoothCode(code) || seen.has(code)) continue;
    const rect = shapeAttrsToBoothRect(tag, attrs, vb);
    if (!rect) continue;
    seen.add(code);
    out.push({ ...rect, code });
  }

  return out;
}

/**
 * Pair booth numbers with cell geometry.
 * Prefers semantic data-stand-id shapes (black/line-art schemes).
 * Else label-first: each A### finds the pastel/outline cell that contains it
 * (smallest area), otherwise nearest cell, otherwise a default pin box on the label.
 */
export function parseLabeledBoothRectsFromSvg(
  svg: string,
  options?: { fill?: string; maxDistPct?: number },
): LabeledBoothRect[] {
  const semantic = parseSemanticStandRectsFromSvg(svg);
  // Enough tagged stands → trust them (PCV black schemes, reconstructed exports).
  if (semantic.length >= 3) return semantic;

  const rects = parseBoothRectsFromSvg(svg, { fill: options?.fill });
  const labels = parseBoothTextLabelsFromSvg(svg);
  if (!labels.length) return [];

  // Line-art plans often have larger cells than pastel Corel sheets.
  const maxDist =
    options?.maxDistPct ?? (rects.length && !svgHasPastelBoothLayer(svg) ? 8 : 5);
  const usedRects = new Set<number>();
  const out: LabeledBoothRect[] = [];
  const seenCodes = new Set<string>();

  for (const lb of labels) {
    if (!lb.code || seenCodes.has(lb.code)) continue;

    let bestIdx = -1;
    let bestArea = Number.POSITIVE_INFINITY;
    for (let i = 0; i < rects.length; i++) {
      if (usedRects.has(i)) continue;
      const r = rects[i]!;
      const pad = 0.4;
      const inside =
        lb.x >= r.x - pad &&
        lb.x <= r.x + r.w + pad &&
        lb.y >= r.y - pad &&
        lb.y <= r.y + r.h + pad;
      if (!inside) continue;
      const area = Math.max(0.01, r.w * r.h);
      if (area < bestArea) {
        bestArea = area;
        bestIdx = i;
      }
    }

    if (bestIdx < 0) {
      let bestD = Number.POSITIVE_INFINITY;
      for (let i = 0; i < rects.length; i++) {
        if (usedRects.has(i)) continue;
        const r = rects[i]!;
        const d = Math.hypot(lb.x - r.cx, lb.y - r.cy);
        if (d < bestD) {
          bestD = d;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestD > maxDist) {
        const w = 3.2;
        const h = 2.6;
        seenCodes.add(lb.code);
        out.push({
          code: lb.code,
          x: Number(Math.max(0, Math.min(100 - w, lb.x - w / 2)).toFixed(2)),
          y: Number(Math.max(0, Math.min(100 - h, lb.y - h / 2)).toFixed(2)),
          w,
          h,
          cx: Number(lb.x.toFixed(2)),
          cy: Number(lb.y.toFixed(2)),
        });
        continue;
      }
    }

    usedRects.add(bestIdx);
    seenCodes.add(lb.code);
    const r = rects[bestIdx]!;
    out.push({ ...r, code: lb.code });
  }

  return out;
}

/**
 * Remove baked-in booth / pavilion text from floorplan SVG.
 * Visitor map shows our pins (logo + booth) instead of upstream Corel labels.
 */
export function stripFloorplanTextLabels(svg: string): string {
  let s = String(svg ?? "");
  if (!s) return s;
  // Remove <text>…</text> (incl. nested tspan) and standalone <tspan>.
  s = s.replace(/<text\b[^>]*>[\s\S]*?<\/text>/gi, "");
  s = s.replace(/<tspan\b[^>]*>[\s\S]*?<\/tspan>/gi, "");
  // Some Corel exports put labels in <text …/> self-closing (rare) or <textPath>.
  s = s.replace(/<textPath\b[^>]*>[\s\S]*?<\/textPath>/gi, "");
  return s;
}

/** Apply crop insets to an SVG root viewBox; returns modified SVG string. */
export function applyFloorplanCrop(svg: string, crop?: FloorplanCropInsets | null): string {
  const c = normalizeCropInsets(crop);
  if (c.left === 0 && c.top === 0 && c.right === 0 && c.bottom === 0) return svg;

  let s = String(svg ?? "");
  const vb = readRootViewBox(s);
  if (!vb) return s;

  const nx = vb.x + (vb.w * c.left) / 100;
  const ny = vb.y + (vb.h * c.top) / 100;
  const nw = vb.w * (1 - (c.left + c.right) / 100);
  const nh = vb.h * (1 - (c.top + c.bottom) / 100);
  if (nw <= 0 || nh <= 0) return s;

  const next = `${nx} ${ny} ${nw} ${nh}`;
  if (/viewBox\s*=/i.test(s)) {
    s = s.replace(/viewBox\s*=\s*["'][^"']*["']/i, `viewBox="${next}"`);
  }
  return s;
}

const GRID_STROKE_RE = /stroke\s*=\s*["']rgb\(\s*180\s*,\s*204\s*,\s*214\s*\)["']/i;
const DECOR_FILL_RE =
  /fill\s*=\s*["'](?:rgb\(\s*177\s*,\s*201\s*,\s*0\s*\)|rgb\(\s*227\s*,\s*0\s*,\s*22\s*\)|#b1c900|#e30016)["']/i;

function classifyLoShapeGroup(className: string, inner: string): UnderlaySvgLayerId {
  if (/TextShape|SVGTextShape|TextParagraph/i.test(className)) return "texts";
  if (/Graphic/i.test(className)) return "decor";
  if (/LineShape/i.test(className)) {
    return GRID_STROKE_RE.test(inner) ? "grid" : "contours";
  }
  if (/PolyLineShape|OpenBezierShape/i.test(className)) return "contours";
  if (/PolyPolygonShape|ClosedBezierShape/i.test(className)) {
    return DECOR_FILL_RE.test(inner) ? "decor" : "fills";
  }
  return "fills";
}

/** Drop LibreOffice hit-test BoundingBox rects (invisible scaffolding, ~1.8k empties). */
export function stripSvgBoundingBoxes(svg: string): string {
  return String(svg ?? "").replace(
    /<rect\b[^>]*\bclass\s*=\s*["']BoundingBox["'][^>]*\/?>/gi,
    "",
  );
}

/**
 * Tag LibreOffice shape groups with data-mvk-ulayer so the UI can toggle
 * grid / black contours / fills / texts / decor inside the underlay.
 * Uses a look-ahead window (nested </g> parsing is unreliable on LO SVG).
 */
export function annotateUnderlaySvgLayers(svg: string): string {
  const raw = String(svg ?? "");
  if (!raw || /\bdata-mvk-ulayer=/.test(raw)) return raw;
  return raw.replace(
    /<g\b([^>]*\bclass\s*=\s*["']([^"']+)["'][^>]*)>/gi,
    (full, attrs: string, cls: string, offset: number) => {
      if (/\bdata-mvk-ulayer\s*=/.test(attrs)) return full;
      if (
        !/com\.sun\.star\.drawing\.(LineShape|PolyPolygonShape|PolyLineShape|ClosedBezierShape|OpenBezierShape)|(?:^|[\s"'])TextShape|SVGTextShape|Graphic/i.test(
          cls,
        )
      ) {
        return full;
      }
      const look = raw.slice(offset, offset + 1200);
      const layer = classifyLoShapeGroup(cls, look);
      return `<g data-mvk-ulayer="${layer}"${attrs}>`;
    },
  );
}

export function resolveUnderlaySvgLayers(
  layers?: UnderlaySvgLayerFlags | null,
): Record<UnderlaySvgLayerId, boolean> {
  return {
    ...DEFAULT_UNDERLAY_SVG_LAYERS,
    ...(layers ?? {}),
  };
}

/** Apply display:none to underlay slices the admin turned off. */
export function applyUnderlaySvgLayerVisibility(
  svg: string,
  layers?: UnderlaySvgLayerFlags | null,
): string {
  const flags = resolveUnderlaySvgLayers(layers);
  let s = annotateUnderlaySvgLayers(String(svg ?? ""));
  s = s.replace(
    /<g\b([^>]*\bdata-mvk-ulayer\s*=\s*["']([^"']+)["'][^>]*)>/gi,
    (full, attrs: string, layerRaw: string) => {
      const layer = layerRaw as UnderlaySvgLayerId;
      if (!(layer in DEFAULT_UNDERLAY_SVG_LAYERS)) return full;
      const on = flags[layer] !== false;
      let a = String(attrs).replace(/\sdisplay\s*=\s*["'][^"']*["']/i, "");
      if (!on) a += ' display="none"';
      return `<g${a}>`;
    },
  );
  return s;
}

/**
 * Prepare floorplan SVG for map rendering (inline or &lt;img blob&gt;).
 *
 * Important: do NOT use width/height="100%" — browsers give &lt;img&gt; a 300×150
 * default intrinsic size and the plan often paints blank.
 */
export function prepareFloorplanSvg(svg: string, options?: PrepareFloorplanOptions): string {
  let s = String(svg ?? "").replace(/^\uFEFF/, "").trim();
  // HTML parsers drop everything when <?xml / DOCTYPE precede <svg> — cut to root.
  const svgStart = s.search(/<svg\b/i);
  if (svgStart < 0) return s;
  if (svgStart > 0) s = s.slice(svgStart);

  // Drop duplicate coral booth layer when pastel cells are present.
  s = stripLegacyCoralBoothFills(s);
  // Drop LO BoundingBox scaffolding (empty invisible rects that still pollute DOM).
  s = stripSvgBoundingBoxes(s);
  // Tag shape groups for layer toggles; hide contours by default unless overridden.
  s = applyUnderlaySvgLayerVisibility(s, options?.underlayLayers);

  s = applyFloorplanCrop(s, options?.crop);

  const fromOpts =
    options?.width && options?.height && options.width > 0 && options.height > 0
      ? { width: Math.round(options.width), height: Math.round(options.height) }
      : null;
  const fromViewBox = readRootViewBoxSize(s);
  const fromPixels = readRootPixelSize(s);
  // Board pixel size must match viewBox aspect — otherwise preserveAspectRatio=none
  // (needed for %-pins) stretches the pavilion when crop / wrong W×H diverge.
  let size = fromOpts ?? fromViewBox ?? fromPixels ?? { width: 1000, height: 700 };
  if (fromViewBox && fromViewBox.width > 0 && fromViewBox.height > 0) {
    const aspect = fromViewBox.width / fromViewBox.height;
    const curAspect = size.width / Math.max(1, size.height);
    if (Math.abs(curAspect - aspect) / aspect > 0.008) {
      const long = Math.max(size.width, size.height);
      size =
        aspect >= 1
          ? clampBoardSize(long, long / aspect, 5000)
          : clampBoardSize(long * aspect, long, 5000);
    }
  }

  if (/preserveAspectRatio\s*=/i.test(s)) {
    s = s.replace(/preserveAspectRatio\s*=\s*["'][^"']*["']/i, 'preserveAspectRatio="none"');
  } else {
    s = s.replace(/<svg\b/i, '<svg preserveAspectRatio="none"');
  }

  if (/viewBox\s*=/i.test(s)) {
    // keep author's coordinate system (possibly cropped)
  } else {
    s = s.replace(
      /<svg\b/i,
      `<svg viewBox="0 0 ${size.width} ${size.height}"`,
    );
  }

  if (/\bwidth\s*=/i.test(s)) {
    s = s.replace(/\bwidth\s*=\s*["'][^"']*["']/i, `width="${size.width}"`);
  } else {
    s = s.replace(/<svg\b/i, `<svg width="${size.width}"`);
  }

  if (/\bheight\s*=/i.test(s)) {
    s = s.replace(/\bheight\s*=\s*["'][^"']*["']/i, `height="${size.height}"`);
  } else {
    s = s.replace(/<svg\b/i, `<svg height="${size.height}"`);
  }

  return s;
}
